(function () {
  "use strict";

  // Known-customer recognition for signup-offer suppression.
  //
  // Klaviyo's own visitor cookie is JS-set, so Safari caps it at ~7 days and
  // existing customers end up being shown the "15% off your first order"
  // signup flyout. This script remembers "this browser belongs to an existing
  // customer" across every signal we control, then:
  //   a) re-identifies the browser to Klaviyo (when we have an email) so its
  //      native "hide from existing profiles" form targeting works again, and
  //   b) hard-hides the signup flyout locally as belt-and-braces.
  //
  // Signals, most→least durable: Shopify login (server cookie, survives ITP);
  // arrival from a Klaviyo email (_kx param); the worker's device-token
  // ledger (/recognize) recovering state after local storage decays.
  //
  // Since 0.5.0 the email click also IDENTIFIES: the landing page's _kx goes
  // to the worker, which resolves it with the merchant's Klaviyo key and
  // accepts it only when that profile just clicked an email (worker
  // lib/kx.js). No template tagging needed; qbk stays as proof-grade hardening.
  //
  // Since 0.5.3 every ping also carries the shopper's answer under Shopify's
  // Customer Privacy API (a/m/p/s, the effective permissions), and a change
  // of answer (the visitorConsentCollected event) is sent at once, so the
  // worker's server-side ad sends can honour a decline (worker
  // lib/device-link.js). The checkout pixel does the same from checkout.
  //
  // Since 0.6.0 (cookie consent, the lightweight version Matt chose 7 Oct;
  // 0.6.1 reads /config itself when no drawer announces it; 0.6.2 keeps an
  // answer given before the config arrived and logs it then):
  // Shopify's own cookie banner stays the consent UI. When the merchant turns
  // the feature on (worker /config `consent`, lib/consent-config.js), this
  // script holds Klaviyo's popup and flyout forms while Shopify's banner is
  // unanswered and shows them afterwards only if marketing is allowed; logs
  // the banner shown (once a session) and every answer to the worker's
  // consent log (POST /consent, the proof-of-consent record); and offers
  // QB.consent.showPreferences(), which re-opens Shopify's own preferences
  // dialog, behind the drawer's "Privacy choices" entry and any merchant link
  // marked data-qb-privacy-choices. Nothing here captures consent itself.
  //
  // 0.7.3: opening the store with #qb-y shows Shopify's visitor id for this
  // browser in a popup, in a field it can be copied from (the first-party
  // Safari measurement, brief §6, on a device that refuses to run a
  // bookmarklet). Read-only; nothing is sent.
  //
  // 0.7.2: multi-market. A store on several domains (Shopify Markets) has a
  // hub hostname per domain (/config edge.hosts beside edge.host); each page
  // uses the one on its own site, so lakaifootwear.com.au's calls stay
  // first-party there as lakai.co.nz's do. A domain without one uses edge.host.
  //
  // 0.7.1: the first-party release of 0.7.0 (one version, one blob: RELEASES).
  //
  // 0.7.0 (first-party build, package A): a browser whose storage Safari
  // wiped is a stranger to this script, but the checkout pixel's page_viewed
  // ping carries Shopify's own visitor id and the worker may re-attach the
  // old dossier a moment after our first ping (worker lib/shopify-client.js),
  // so an unknown device looks once more a little later — only on a store
  // whose /config says the pixel is live (`pixel: true`); a custom-app tenant
  // without one (Lakai) never pays for a look that cannot succeed.

  var VERSION = "0.7.3";
  var RELINK_RECHECK_MS = 2500; // the pixel's anchor ping has usually landed by then
  var pixelLive = Boolean(window.QB && window.QB.pixel === true);

  // ── Transport (0.7.0, first-party build package T) ─────────────────────────
  //
  // Where each call to the worker goes, decided per call from /config's `edge`
  // block ({ host, hosts, proxyPath }, all optional, absent until the merchant
  // set them up; worker lib/edge-config.js):
  //   - the SIGNED-IN register ping -> location.origin + proxyPath + path, when
  //     proxyPath is set: Shopify's app proxy forwards it to our edge with a
  //     signed logged_in_customer_id, which the worker treats as proof (B);
  //   - everything else -> https://<host> when a custom hostname is verified (C):
  //     of host and hosts, the first on this page's own site (0.7.2), else host;
  //   - otherwise -> the workers.dev origin (QB.worker.url, "direct").
  // /config itself is never fetched through the proxy (slower, and the proxy is
  // for proof, not for config). A failure on the proxy or the custom host — a
  // network error, a 404, a 5xx — marks the rest of the page view "direct", so
  // there are never two failed calls in a row; the failed call itself is
  // retried direct only when that cannot do the work twice: the edge said it
  // never reached the tenant (X-QB-Edge-Refused: 1, which only the edge sets),
  // or the method is GET/HEAD, or it is a POST to an idempotent path
  // (/recognize, /track, /consent) that failed on the network. A tenant 5xx or
  // a lost reply on POST /redeem, /subscribe, /chat… may mean the tenant
  // already did the work (money, a message, a ticket), so that result goes
  // back to the caller as it is (review of 89642a8). The
  // edge block is kept in localStorage so the very next page view routes
  // correctly before /config has answered; a /config that no longer carries it
  // clears the memory. Every slot script calls QB.transport.fetch(url, init)
  // with the direct URL it always built, and this rewrites the origin; a URL
  // that is not the worker's goes to fetch untouched.
  var EDGE_STORE_KEY = "qb_edge";
  var EDGE_STORE_TTL_MS = 86400000;
  var EDGE_HOST_RE = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
  var EDGE_PROXY_RE = /^\/apps\/[a-z0-9-]{1,64}$/;
  var EDGE_HOSTS_MAX = 10;
  var edgeConfig = null;
  var edgeDegraded = false;
  var EDGE_REFUSED_HEADER = "X-QB-Edge-Refused";
  var IDEMPOTENT_POSTS = { "/recognize": true, "/track": true, "/consent": true };

  function sanitizeEdge(raw) {
    if (!raw || typeof raw !== "object") return null;
    var out = {};
    if (typeof raw.host === "string" && EDGE_HOST_RE.test(raw.host)) out.host = raw.host;
    if (Object.prototype.toString.call(raw.hosts) === "[object Array]") {
      var hosts = [];
      for (var i = 0; i < raw.hosts.length && hosts.length < EDGE_HOSTS_MAX; i++) {
        var h = raw.hosts[i];
        if (typeof h === "string" && EDGE_HOST_RE.test(h) && h !== out.host && hosts.indexOf(h) === -1) hosts.push(h);
      }
      if (hosts.length) out.hosts = hosts;
    }
    if (typeof raw.proxyPath === "string" && EDGE_PROXY_RE.test(raw.proxyPath)) out.proxyPath = raw.proxyPath;
    return out.host || out.hosts || out.proxyPath ? out : null;
  }

  // Is hub.example.com on this page's site? Its parent (example.com) is the
  // page's host or a parent of it (www.example.com, shop.example.com).
  function onPageSite(host) {
    var page = String((location && location.hostname) || "").toLowerCase();
    var parent = host.slice(host.indexOf(".") + 1);
    return page === parent || page.slice(-(parent.length + 1)) === "." + parent;
  }

  // The custom hostname this page uses: its own site's, else the main one.
  function edgeHost() {
    var all = (edgeConfig.host ? [edgeConfig.host] : []).concat(edgeConfig.hosts || []);
    for (var i = 0; i < all.length; i++) if (onPageSite(all[i])) return all[i];
    return edgeConfig.host || null;
  }

  function readEdgeStore() {
    try {
      var hit = JSON.parse(localStorage.getItem(EDGE_STORE_KEY));
      if (!hit || !(hit.at > Date.now() - EDGE_STORE_TTL_MS)) return null;
      return sanitizeEdge(hit.edge);
    } catch (e) {
      return null;
    }
  }

  function writeEdgeStore(edge) {
    try {
      if (edge) localStorage.setItem(EDGE_STORE_KEY, JSON.stringify({ edge: edge, at: Date.now() }));
      else localStorage.removeItem(EDGE_STORE_KEY);
    } catch (e) {
      /* no storage: this page view still routes from what /config said */
    }
  }

  // Called with /config's `edge` (or undefined/null when the response has none).
  function applyEdgeConfig(raw) {
    edgeConfig = sanitizeEdge(raw);
    writeEdgeStore(edgeConfig);
  }

  function directOrigin() {
    var u = window.QB && window.QB.worker && window.QB.worker.url;
    return typeof u === "string" ? u.replace(/\/+$/, "") : "";
  }

  // The worker path inside a direct URL, or null for any other URL.
  function workerPathOf(url) {
    var direct = directOrigin();
    if (!direct || typeof url !== "string" || url.indexOf(direct) !== 0) return null;
    var rest = url.slice(direct.length);
    return rest === "" || rest.charAt(0) === "/" ? rest || "/" : null;
  }

  function transportTarget(path, opts) {
    var direct = directOrigin();
    if (edgeDegraded || !edgeConfig) return direct;
    var pathname = path.split("?")[0];
    if (opts && opts.signedIn && edgeConfig.proxyPath && pathname !== "/config") return location.origin + edgeConfig.proxyPath;
    var host = edgeHost();
    if (host) return "https://" + host;
    return direct;
  }

  // May the failed edge call be repeated direct without doing work twice?
  // `res` is the edge leg's response (null on a network failure).
  function retrySafe(path, init, res) {
    try {
      if (res && res.headers && typeof res.headers.get === "function" && res.headers.get(EDGE_REFUSED_HEADER) === "1") return true;
    } catch (e) {
      /* no headers to read: judge by the method */
    }
    var method = String((init && init.method) || "GET").toUpperCase();
    if (method === "GET" || method === "HEAD") return true;
    var pathname = path.split("?")[0];
    return method === "POST" && !res && Boolean(IDEMPOTENT_POSTS[pathname]);
  }

  function transportFetch(url, init, opts) {
    var path = workerPathOf(url);
    if (path === null) return fetch(url, init);
    var direct = directOrigin();
    var base = transportTarget(path, opts);
    if (base === direct) return fetch(direct + path, init);
    return fetch(base + path, init).then(
      function (res) {
        if (!res || !(res.status === 404 || res.status >= 500)) return res;
        edgeDegraded = true;
        return retrySafe(path, init, res) ? fetch(direct + path, init) : res;
      },
      function (err) {
        edgeDegraded = true;
        if (retrySafe(path, init, null)) return fetch(direct + path, init);
        throw err;
      }
    );
  }

  edgeConfig = readEdgeStore();
  window.QB = window.QB || {};
  window.QB.transport = {
    version: VERSION,
    fetch: transportFetch,
    // The origin a given worker path would go to right now (for anything that must build a URL itself).
    target: function (path, opts) {
      return transportTarget(typeof path === "string" ? path : "/", opts);
    },
    direct: directOrigin,
    edge: function () {
      return edgeConfig;
    },
    degraded: function () {
      return edgeDegraded;
    },
    apply: applyEdgeConfig,
  };
  var STORE_KEY = "qb_known";
  var COOKIE = "qb_known";
  var TOKEN_KEY = "qb_device_token";
  // Mirror of TOKEN_KEY, readable by the checkout web pixel. Keep in step with
  // TOKEN_COOKIE in shopify-app/extensions/qb-pixel/src/index.js.
  var TOKEN_COOKIE = "qb_dt";
  var MAX_AGE = 34560000; // 400 days — the browser cap on cookie lifetime
  var SUPPRESS_FORM_IDS = ["QPaLCu"]; // Klaviyo "Email Flyout" signup form
  // Signed identity token from our own outbound links. Keep in step with
  // LINK_TOKEN_PARAM and the token format in worker/lib/link-token.js.
  var LINK_PARAM = "qbk";
  var LINK_TOKEN_RE = /^\d{1,20}\.\d{1,12}\.[A-Za-z0-9_-]{20,64}$/;
  // Klaviyo's exchange id on every tracked email/SMS link: <ciphertext>.<public key>.
  // Keep in step with KX_RE in worker/lib/kx.js (50 and 71 chars seen; never a fixed length).
  var KX_PARAM = "_kx";
  var KX_RE = /^[A-Za-z0-9_=-]{20,128}\.[A-Za-z0-9]{6}$/;

  function readStore() {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || null;
    } catch (e) {
      return null;
    }
  }

  function writeStore(data) {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(data));
    } catch (e) {
      /* storage unavailable — cookie below still carries the flag */
    }
    document.cookie = COOKIE + "=1; Max-Age=" + MAX_AGE + "; Path=/; SameSite=Lax; Secure";
  }

  function hasCookie() {
    return document.cookie.indexOf(COOKIE + "=1") !== -1;
  }

  function getToken() {
    var t = null;
    try {
      t = localStorage.getItem(TOKEN_KEY);
    } catch (e) {
      /* fall through */
    }
    // The cookie is the fallback AND the channel to checkout. A theme app
    // extension does not run on checkout pages, so the web pixel is the only
    // thing there — and a strict-sandbox pixel cannot reach the page's
    // localStorage. It can read cookies on the shop domain, so the token is
    // mirrored into one. Safari caps a JS-set cookie at 7 days, which is
    // irrelevant here: browse-to-checkout is minutes.
    if (!t) t = readCookie(TOKEN_COOKIE);
    if (!t) {
      t =
        window.crypto && crypto.randomUUID
          ? crypto.randomUUID()
          : Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 12);
      try {
        localStorage.setItem(TOKEN_KEY, t);
      } catch (e) {
        /* token won't persist; recognition degrades gracefully */
      }
    }
    try {
      document.cookie = TOKEN_COOKIE + "=" + encodeURIComponent(t) + "; Max-Age=" + MAX_AGE + "; Path=/; SameSite=Lax; Secure";
    } catch (e) {
      /* no cookie: the pixel simply cannot join this checkout to this device */
    }
    return t;
  }

  function readCookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Rewrite the address bar without the link token, leaving everything else
  // (including Klaviyo's _kx and any UTM tags) untouched. _kx must stay:
  // klaviyo.js loads async and reads it from the URL after this script runs,
  // for its own identification and Active on Site attribution. replaceState so the
  // back button is not polluted; a browser without it just keeps the param.
  function stripLinkParam() {
    try {
      if (!window.history || !history.replaceState) return;
      var url = new URL(location.href);
      if (!url.searchParams.has(LINK_PARAM)) return;
      url.searchParams.delete(LINK_PARAM);
      history.replaceState(history.state, "", url.pathname + (url.search || "") + (url.hash || ""));
    } catch (e) {
      /* old browser or opaque origin — the token simply stays visible */
    }
  }

  // Ad-platform browser IDs for the worker's first-party dossier — captured
  // at click time so later server-side conversions (Meta CAPI / Google) can
  // be sent with strong match keys. Pseudonymous IDs only, no PII.
  function collectAdIds() {
    var ads = {};
    var p = new URLSearchParams(location.search);
    var fbp = readCookie("_fbp");
    if (fbp) ads.fbp = fbp;
    var fbc = readCookie("_fbc");
    if (!fbc && p.get("fbclid")) fbc = "fb.1." + Date.now() + "." + p.get("fbclid");
    if (fbc) ads.fbc = fbc;
    ["gclid", "gbraid", "wbraid"].forEach(function (k) {
      if (p.get(k)) ads[k] = p.get(k);
    });
    var ga = readCookie("_ga"); // "GA1.1.123456789.1712345678" → "123456789.1712345678"
    if (ga) {
      var parts = ga.split(".");
      if (parts.length >= 4) ads.gaClientId = parts[parts.length - 2] + "." + parts[parts.length - 1];
    }
    return Object.keys(ads).length ? ads : null;
  }

  // Respect Shopify's consent framework when one is configured; stores
  // without a banner (no API present) fall through to the store default.
  function marketingConsentAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === "function") return cp.marketingAllowed() !== false;
    } catch (e) {
      /* treat errors as no framework */
    }
    return true;
  }

  // Naming WHO a browser belongs to from an email link (qbk, Klaviyo's _kx) is
  // used for attribution, so it follows the shopper's analytics/marketing
  // choice where the store collects one (since 0.5.2; EU/UK-ready for the
  // public app). Declined: the visit is still recognised as "somebody we know"
  // (functional: it only hides first-order offers), and a signed-in shopper is
  // still themselves. No consent API present: the store default, as above.
  function identificationConsentAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return true;
      var analytics = typeof cp.analyticsProcessingAllowed === "function" ? cp.analyticsProcessingAllowed() !== false : null;
      var marketing = typeof cp.marketingAllowed === "function" ? cp.marketingAllowed() !== false : null;
      if (analytics === null && marketing === null) return true;
      return analytics === true || marketing === true;
    } catch (e) {
      return true;
    }
  }

  // The four effective permissions, for the worker's dossier: null when the
  // store has no consent API loaded (then nothing is sent and the worker keeps
  // its "unknown" default). Shopify answers these from the regional default
  // when the shopper has not been asked, so a notice-mode visitor reads as
  // allowed and an unanswered opt-in visitor as declined, which is the point.
  function consentSnapshot() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return null;
      var out = {};
      if (typeof cp.analyticsProcessingAllowed === "function") out.a = cp.analyticsProcessingAllowed() !== false;
      if (typeof cp.marketingAllowed === "function") out.m = cp.marketingAllowed() !== false;
      if (typeof cp.preferencesProcessingAllowed === "function") out.p = cp.preferencesProcessingAllowed() !== false;
      if (typeof cp.saleOfDataAllowed === "function") out.s = cp.saleOfDataAllowed() !== false;
      return Object.keys(out).length ? out : null;
    } catch (e) {
      return null;
    }
  }

  // The visitorConsentCollected detail names the categories slightly
  // differently from the API's methods; read either spelling.
  function consentFromEvent(detail) {
    if (!detail || typeof detail !== "object") return null;
    var out = {};
    var a = detail.analyticsAllowed !== undefined ? detail.analyticsAllowed : detail.analyticsProcessingAllowed;
    var p = detail.preferencesAllowed !== undefined ? detail.preferencesAllowed : detail.preferencesProcessingAllowed;
    if (typeof a === "boolean") out.a = a;
    if (typeof detail.marketingAllowed === "boolean") out.m = detail.marketingAllowed;
    if (typeof p === "boolean") out.p = p;
    if (typeof detail.saleOfDataAllowed === "boolean") out.s = detail.saleOfDataAllowed;
    return Object.keys(out).length ? out : null;
  }

  function suppressForms() {
    if (document.getElementById("qb-known-css")) return;
    var css =
      SUPPRESS_FORM_IDS.map(function (id) {
        return ".klaviyo-form-" + id;
      }).join(", ") + " { display: none !important; }";
    var style = document.createElement("style");
    style.id = "qb-known-css";
    style.textContent = css;
    document.head.appendChild(style);
  }

  function identifyToKlaviyo(email) {
    if (!email) return;
    var attempts = 0;
    (function tryIdentify() {
      attempts += 1;
      try {
        if (window.klaviyo && typeof window.klaviyo.identify === "function") {
          window.klaviyo.identify({ email: email });
          return;
        }
        if (window._learnq && typeof window._learnq.push === "function") {
          window._learnq.push(["identify", { $email: email }]);
          return;
        }
      } catch (e) {
        return;
      }
      if (attempts < 10) setTimeout(tryIdentify, 1000);
    })();
  }

  function ping(workerUrl, payload, onKnown) {
    try {
      // The signed-in register ping is the one call that may take Shopify's
      // app proxy (QB.transport above): Shopify then vouches for the customer id.
      var signedIn = Boolean(payload && payload.register === true && payload.customerId);
      transportFetch(
        workerUrl + "/recognize",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
        },
        { signedIn: signedIn }
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          if (data && data.known && onKnown) onKnown();
        })
        .catch(function () {
          /* recognition is best-effort */
        });
    } catch (e) {
      /* ignore */
    }
  }

  var customer = (window.QB && window.QB.customer) || {};
  var workerUrl = window.QB && window.QB.worker && window.QB.worker.url;
  var params = new URLSearchParams(location.search);
  var fromEmail =
    params.has("_kx") || (params.get("utm_source") || "").toLowerCase() === "klaviyo";

  // A link token we minted and signed (worker lib/link-token.js), carried by
  // links in email we send. `_kx` proves the visitor is SOMEBODY we know;
  // this proves WHO, so the worker can attach the device to a customer id
  // without the browser being able to claim one. Shape-checked only here —
  // the signature is verified server-side, never in the page.
  var linkToken = params.get(LINK_PARAM);
  if (linkToken && !LINK_TOKEN_RE.test(linkToken)) linkToken = null;

  // Klaviyo's _kx, for the worker to resolve (above). URLSearchParams has
  // already decoded %3D to =. Never stripped from the URL.
  var kx = params.get(KX_PARAM);
  if (kx && !KX_RE.test(kx)) kx = null;

  var stored = readStore();
  var known = Boolean(stored && stored.known) || hasCookie();
  var signal = customer.loggedIn === true || fromEmail || Boolean(linkToken);

  var adIds = marketingConsentAllowed() ? collectAdIds() : null;
  var consent = consentSnapshot();

  // Take the token out of the visible URL as soon as it has been read. It
  // identifies a person, so it should not be shared, bookmarked, indexed, or
  // handed to a third party in a Referer header.
  if (linkToken) stripLinkParam();

  // What this page view asserts about who the visitor is. The late ad-id ping
  // below repeats it: KV (the worker's dossier store) is eventually consistent
  // and briefly caches "not found", so a ping a few seconds after the landing
  // ping can read no dossier and write a fresh unknown one over the
  // identification (seen live 7 Oct 2026 from an AU edge). Repeating the
  // signals makes the late ping re-identify instead of erase.
  // Computed per ping from the CURRENT answer, never once at load: a shopper
  // who lands from an email while allowed and then declines on the page must
  // not have the re-ping carry the link identifiers the decline just refused
  // (review of 0.5.3), and one who accepts after load may add them. `answer`
  // is the consent the ping is about to send (the event's detail, when the
  // API itself has not caught up yet); without one the API is asked.
  function identityFor(answer) {
    if (!signal) return null;
    var may;
    if (answer && (typeof answer.a === "boolean" || typeof answer.m === "boolean")) {
      may = answer.a === true || answer.m === true;
    } else {
      may = identificationConsentAllowed();
    }
    return {
      register: true,
      customerId: customer.loggedIn ? String(customer.id) : undefined,
      linkToken: (may && linkToken) || undefined,
      kx: (may && kx) || undefined,
      kxFrom: may && kx ? "url" : undefined,
    };
  }

  function withIdentity(payload) {
    var identity = identityFor(payload.consent);
    if (!identity) return payload;
    for (var k in identity) {
      if (identity[k] !== undefined) payload[k] = identity[k];
    }
    return payload;
  }

  if (signal) {
    var data = stored || {};
    data.known = true;
    if (customer.loggedIn) {
      data.customerId = customer.id;
      data.email = customer.email;
    }
    data.t = Date.now();
    writeStore(data);
    stored = data;
    known = true;
    if (workerUrl) {
      ping(workerUrl, withIdentity({ token: getToken(), ads: adIds || undefined, consent: consent || undefined }));
    }
  }

  function becomeKnown() {
    known = true;
    if (window.QB && window.QB.known) window.QB.known.isKnown = true;
    suppressForms();
    try {
      document.dispatchEvent(new CustomEvent("qb:known"));
    } catch (e) {
      /* ancient browser — slots just won't live-update */
    }
  }

  if (known) {
    suppressForms();
    identifyToKlaviyo((stored && stored.email) || customer.email || null);
    if (!stored) writeStore({ known: true, t: Date.now() }); // cookie survived a storage wipe
  } else if (workerUrl) {
    // Last resort: the worker's ledger may remember this device token even
    // though local state decayed. Flyouts appear after a delay, so a late
    // suppress still wins the race.
    // The consent rides along even for a stranger: a new visitor who declines
    // marketing and then subscribes must not become a server-side Lead.
    ping(workerUrl, { token: getToken(), ads: adIds || undefined, consent: consent || undefined }, function () {
      writeStore({ known: true, t: Date.now() });
      becomeKnown();
    });
    // Second look (0.7.0): the pixel's Shopify-visitor-id ping may have
    // relinked this fresh token to the old dossier since. One extra ping per
    // still-unknown page view on a store with the pixel live (the config must
    // have said so by then); an all-allowed stranger costs the worker no write.
    setTimeout(function () {
      if (known || !pixelLive) return;
      ping(workerUrl, { token: getToken(), consent: consentSnapshot() || undefined }, function () {
        writeStore({ known: true, t: Date.now() });
        becomeKnown();
      });
    }, RELINK_RECHECK_MS);
  }

  // ── Cookie consent (0.6.0): Shopify's banner, the Hub around it ──────────
  //
  // consentConfig arrives from /config (qb-drawer.js fetches it once a page
  // view and dispatches "qb:config"); until it does, or while the feature is
  // off, nothing below runs — a store that has not switched consent on sees
  // no change on upgrade.
  var CONSENT_SHOWN_KEY = "qb_consent_shown"; // sessionStorage: the banner was logged as shown this session
  var CONSENT_LAST_KEY = "qb_consent_last"; // sessionStorage: the last answer logged, so a re-save is one row
  var HOLD_CLASS = "qb-consent-hold";
  var HOLD_CSS_ID = "qb-consent-hold-css";
  var consentConfig = (window.QB && window.QB.consentConfig) || null;
  var holdActive = false;
  var holdObserver = null;

  function privacyApi() {
    return (window.Shopify && window.Shopify.customerPrivacy) || null;
  }

  // Shopify's banner is on screen and unanswered: consent is required in the
  // visitor's region and none is recorded. The regional rules are Shopify's.
  function bannerPending() {
    try {
      var cp = privacyApi();
      return Boolean(cp && typeof cp.shouldShowBanner === "function" && cp.shouldShowBanner());
    } catch (e) {
      return false;
    }
  }

  function regionCode() {
    try {
      var cp = privacyApi();
      var r = cp && typeof cp.getRegion === "function" ? cp.getRegion() : "";
      return typeof r === "string" ? r : "";
    } catch (e) {
      return "";
    }
  }

  function sessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* no sessionStorage — the worker still dedupes nothing, so log anyway */
    }
  }

  function postConsent(body) {
    if (!workerUrl) return;
    try {
      body.token = getToken();
      body.region = regionCode();
      transportFetch(workerUrl + "/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(function () {
        /* the log is best effort; Shopify's record and the dossier still hold */
      });
    } catch (e) {
      /* ignore */
    }
  }

  // One "shown" row per session while Shopify's banner is pending: it shows on
  // every page view until answered, and a row per view would be inflation.
  function logShown() {
    if (!consentConfig || !consentConfig.logShown) return;
    if (sessionGet(CONSENT_SHOWN_KEY)) return;
    sessionSet(CONSENT_SHOWN_KEY, "1");
    postConsent({ kind: "shown", mode: "shopify", src: "shopify" });
  }

  // An answer given before the config arrives is kept and logged when it
  // lands, together with the banner it answered. A theme whose drawer never
  // announces the config (Lakai's forked 0.9.1) waits for the 3 s fallback
  // below, and a shopper can click Accept sooner; without this the answer was
  // simply lost, no row at all (joint review, 8 Oct 2026). A config that
  // says the feature is off drops it.
  var answerBeforeConfig = null;

  // One row per distinct answer: Shopify fires visitorConsentCollected on a
  // re-save of the preferences dialog too, and one decision is one row.
  function logAnswer(answer) {
    if (!answer) return;
    if (!consentConfig) {
      answerBeforeConfig = answer;
      return;
    }
    if (!consentConfig.enabled) return;
    var key = JSON.stringify(answer);
    if (sessionGet(CONSENT_LAST_KEY) === key) return;
    sessionSet(CONSENT_LAST_KEY, key);
    postConsent({ kind: "answer", mode: "shopify", src: "shopify", choices: answer });
  }

  // Klaviyo renders every form into a .klaviyo-form-XXXXXX box; the popups
  // and flyouts are the ones in a fixed-position wrapper, inline embeds are
  // not. Only the former are held: a footer signup form is not a pop-up.
  function isPopupForm(el) {
    var node = el;
    for (var i = 0; node && i < 4; i += 1) {
      try {
        if (window.getComputedStyle(node).position === "fixed") return true;
      } catch (e) {
        return false;
      }
      node = node.parentElement;
    }
    return false;
  }

  // Forms already decided (held, or checked and found inline) are not looked
  // at again; the observer's callback is throttled to one pass a frame, since
  // a busy page mutates the body constantly while the hold is on.
  var holdScheduled = false;

  function applyHold() {
    if (!holdActive) return;
    var forms = document.querySelectorAll('[class*="klaviyo-form-"]');
    for (var i = 0; i < forms.length; i += 1) {
      var form = forms[i];
      if (form.classList.contains(HOLD_CLASS) || form.getAttribute("data-qb-hold-checked")) continue;
      if (isPopupForm(form)) form.classList.add(HOLD_CLASS);
      else form.setAttribute("data-qb-hold-checked", "1");
    }
  }

  function scheduleHold() {
    if (holdScheduled || !holdActive) return;
    holdScheduled = true;
    var run = function () {
      holdScheduled = false;
      applyHold();
    };
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
    else setTimeout(run, 16);
  }

  function startHold() {
    if (holdActive) return;
    holdActive = true;
    if (!document.getElementById(HOLD_CSS_ID)) {
      var style = document.createElement("style");
      style.id = HOLD_CSS_ID;
      style.textContent = "." + HOLD_CLASS + " { display: none !important; }";
      document.head.appendChild(style);
    }
    applyHold();
    try {
      if (typeof MutationObserver === "function" && document.body) {
        holdObserver = new MutationObserver(scheduleHold);
        holdObserver.observe(document.body, { childList: true, subtree: true });
      }
    } catch (e) {
      /* without an observer only forms already in the page are held */
    }
  }

  function endHold() {
    holdActive = false;
    if (holdObserver) {
      try {
        holdObserver.disconnect();
      } catch (e) {
        /* ignore */
      }
      holdObserver = null;
    }
    var held = document.querySelectorAll("." + HOLD_CLASS);
    for (var i = 0; i < held.length; i += 1) held[i].classList.remove(HOLD_CLASS);
  }

  // The hold applies while the banner is unanswered AND for as long as
  // marketing is declined: a shopper who said no to marketing gets no
  // marketing pop-up on any later page either (review of 0.6.0). It ends only
  // on an answer that allows marketing.
  function holdWanted() {
    return Boolean(consentConfig && consentConfig.enabled && consentConfig.holdFlyout && privacyApi() && (bannerPending() || !marketingConsentAllowed()));
  }

  function releaseFor(answer) {
    if (!holdActive) return;
    var m = answer && typeof answer.m === "boolean" ? answer.m : marketingConsentAllowed();
    if (m) endHold();
  }

  function applyConsentConfig(cfg) {
    consentConfig = cfg && typeof cfg === "object" ? cfg : null;
    if (!consentConfig || !consentConfig.enabled) {
      answerBeforeConfig = null;
      if (holdActive) endHold();
      return;
    }
    if (!privacyApi() && window.Shopify && typeof window.Shopify.loadFeatures === "function") {
      // The store's own scripts load the API when a banner is configured; ask
      // for it ourselves in case they have not yet, then look again.
      try {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function (err) {
          if (!err && privacyApi()) applyConsentConfig(consentConfig);
        });
      } catch (e) {
        /* no API: no hold, no log — the store default applies */
      }
      return;
    }
    if (!privacyApi()) return; // no API at all: nothing to hold, log or re-open
    if (holdWanted()) startHold();
    // A banner answered before the config came was shown first: both rows.
    if (bannerPending() || answerBeforeConfig) logShown();
    if (answerBeforeConfig) {
      var kept = answerBeforeConfig;
      answerBeforeConfig = null;
      logAnswer(kept);
    }
    // The links to change or withdraw consent matter most to a shopper who
    // has already answered, so they do not depend on the banner being up.
    bindPrivacyLinksWhenAvailable();
  }

  // Shopify's preferences dialog, re-opened from the drawer's entry or any
  // merchant link marked data-qb-privacy-choices (both hidden when the banner
  // script that provides it is absent).
  function showPreferencesAvailable() {
    try {
      return Boolean(window.privacyBanner && typeof window.privacyBanner.showPreferences === "function");
    } catch (e) {
      return false;
    }
  }

  function showPreferences() {
    if (!showPreferencesAvailable()) return false;
    try {
      window.privacyBanner.showPreferences();
      return true;
    } catch (e) {
      return false;
    }
  }

  function bindPrivacyLinks() {
    var links = document.querySelectorAll("[data-qb-privacy-choices]");
    var available = showPreferencesAvailable();
    for (var i = 0; i < links.length; i += 1) {
      var el = links[i];
      el.hidden = !available;
      if (available && consentConfig && consentConfig.linkLabel && el.getAttribute("data-qb-privacy-choices") === "label") {
        el.textContent = consentConfig.linkLabel;
      }
      if (el.getAttribute("data-qb-privacy-bound")) continue;
      el.setAttribute("data-qb-privacy-bound", "1");
      el.addEventListener("click", function (e) {
        if (e && e.preventDefault) e.preventDefault();
        showPreferences();
      });
    }
    return available;
  }

  // Shopify injects its banner script asynchronously, so window.privacyBanner
  // may appear after this script and after /config. Bind now, and look again
  // a few times over the first seconds; the moment the dialog is available,
  // "qb:consent-ready" tells the drawer to render its own entry. Bounded, so
  // a store with no banner script costs four cheap checks and nothing more.
  var BIND_RETRY_MS = [500, 1000, 2000, 4000];
  var bindRetryIndex = 0;
  var readyAnnounced = false;

  function bindPrivacyLinksWhenAvailable() {
    var available = bindPrivacyLinks();
    if (available) {
      if (!readyAnnounced) {
        readyAnnounced = true;
        try {
          document.dispatchEvent(new CustomEvent("qb:consent-ready"));
        } catch (e) {
          /* ancient browser — the drawer's own retry on load still runs */
        }
      }
      return;
    }
    if (bindRetryIndex < BIND_RETRY_MS.length) {
      setTimeout(bindPrivacyLinksWhenAvailable, BIND_RETRY_MS[bindRetryIndex]);
      bindRetryIndex += 1;
    }
  }

  var configArrived = false;
  document.addEventListener("qb:config", function (e) {
    configArrived = true;
    if (e && e.detail && e.detail.pixel === true) pixelLive = true;
    applyEdgeConfig(e && e.detail ? e.detail.edge : null);
    applyConsentConfig(e && e.detail && e.detail.consent);
  });
  if (consentConfig) {
    configArrived = true;
    applyConsentConfig(consentConfig);
  }
  // A theme whose drawer predates 0.14.0 (Lakai's forked 0.9.1) never sends
  // "qb:config". Rather than depend on the drawer, read /config here when
  // nothing has announced it after a moment: the endpoint is public and cached
  // for five minutes, so the second fetch costs nothing worth counting.
  var CONFIG_FALLBACK_MS = 3000;
  if (workerUrl) {
    setTimeout(function () {
      if (configArrived) return;
      try {
        transportFetch(workerUrl + "/config")
          .then(function (res) {
            return res.json();
          })
          .then(function (data) {
            if (configArrived) return;
            configArrived = true;
            if (data && data.pixel === true) pixelLive = true;
            applyEdgeConfig(data ? data.edge : null);
            var cfg = data && data.consent && typeof data.consent === "object" ? data.consent : { enabled: false };
            window.QB = window.QB || {};
            if (!window.QB.consentConfig) window.QB.consentConfig = cfg;
            applyConsentConfig(cfg);
          })
          .catch(function () {
            /* no config: the consent features stay off on this page view */
          });
      } catch (e) {
        /* ignore */
      }
    }, CONFIG_FALLBACK_MS);
  }

  // A change of answer (Shopify's banner or its preferences dialog) reaches
  // the worker at once: marketing newly allowed also sends the ad ids that
  // were held back at load; a decline makes the worker drop the ones it has.
  // The same event logs the answer and releases the flyout hold.
  if (workerUrl) {
    document.addEventListener("visitorConsentCollected", function (e) {
      var changed = consentFromEvent(e && e.detail) || consentSnapshot();
      if (!changed) return;
      consent = changed;
      var late = changed.m !== false ? collectAdIds() : null;
      ping(workerUrl, withIdentity({ token: getToken(), consent: changed, ads: late || undefined }));
      logAnswer(changed);
      releaseFor(changed);
    });
  }

  // Pixels set their cookies asynchronously, so _fbp/_ga often don't exist
  // yet when this script runs on a first pageview. Re-collect after they've
  // had a chance, and check in once per session (or whenever a fresh click
  // ID lands) so the dossier stays current.
  if (workerUrl) {
    setTimeout(function () {
      if (!marketingConsentAllowed()) return;
      var late = collectAdIds();
      if (!late) return;
      var fingerprint = JSON.stringify(late);
      try {
        if (sessionStorage.getItem("qb_ads_sent") === fingerprint) return;
        sessionStorage.setItem("qb_ads_sent", fingerprint);
      } catch (e) {
        /* no sessionStorage — send anyway */
      }
      ping(workerUrl, withIdentity({ token: getToken(), ads: late, consent: consent || undefined }));
    }, 4000);
  }

  window.QB = window.QB || {};
  window.QB.known = {
    version: VERSION,
    isKnown: known,
    // Called by other slots when the visitor identifies themselves (e.g.
    // subscribing in the Deals tab) so signup surfaces stop showing.
    mark: function (detail) {
      var data = readStore() || {};
      data.known = true;
      if (detail && detail.email) data.email = detail.email;
      data.t = Date.now();
      writeStore(data);
      if (workerUrl) ping(workerUrl, { token: getToken(), register: true, consent: consent || undefined });
      becomeKnown();
    },
    // The shopper's current answer (a/m/p/s booleans), or null without a
    // consent API; the slots repeat it in their requests so a decline reaches
    // the worker's senders even before its own record of it is readable.
    consent: function () {
      return consentSnapshot();
    },
  };
  // Cookie consent (0.6.0): what the drawer and merchant links need.
  window.QB.consent = {
    version: VERSION,
    config: function () {
      return consentConfig;
    },
    pending: bannerPending,
    holding: function () {
      return holdActive;
    },
    available: showPreferencesAvailable,
    showPreferences: showPreferences,
    bindLinks: function () {
      return bindPrivacyLinks();
    },
  };

  // ── Shopify visitor-id check (0.7.3) ───────────────────────────────────────
  // https://<store>/#qb-y shows the `_y` server-timing entry Shopify puts on
  // every storefront page (its own visitor id, kept in its HttpOnly cookies
  // since Jan 2026). The first-party build's Safari measurement reads it today
  // and again 8+ days later: the same id means the relink anchor survives
  // Safari's 7-day cap. A bookmarklet does the same, but Screen Time and some
  // managed devices refuse to run one. The id sits in a prompt field so it can
  // be copied (an exact match is the whole test); the answer is ignored. Shown
  // only to the person who typed the address; nothing is stored or sent.
  function showShopifyVisitorId() {
    if (String((location && location.hash) || "").toLowerCase() !== "#qb-y") return;
    var y = null;
    try {
      var nav = performance.getEntriesByType("navigation")[0];
      var timing = (nav && nav.serverTiming) || [];
      for (var i = 0; i < timing.length; i++) if (timing[i].name === "_y") y = timing[i].description;
    } catch (e) {
      /* no Navigation Timing: the message below says so */
    }
    try {
      if (y) window.prompt("Shopify visitor id (copy it):", y);
      else window.alert("No Shopify visitor id on this page. Reload it and try again.");
    } catch (e) {
      /* no dialogs here */
    }
  }
  showShopifyVisitorId();
  try {
    window.addEventListener("hashchange", showShopifyVisitorId);
  } catch (e) {
    /* no events: a fresh load with #qb-y still works */
  }
})();
